import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { Badge } from "@/components/ui/badge";
import { Plus } from "lucide-react";
import Repeater from "@/components/Repeater";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import usePostcodeLookup from "@/lib/usePostcodeLookup";
import { normalizeNlPhone } from "@/lib/format";
import api from "@/lib/api";
import { toast } from "sonner";

const GENDERS = ["Man", "Vrouw", "Onbekend"];
const BLOCKS = [
  ["correspondence", "Correspondentieadres"],
  ["employer", "Werkgever"],
  ["guarantor", "Garantsteller"],
  ["administrator", "Bewindvoerder"],
];
const COMPANY_BLOCKS = ["employer", "guarantor", "administrator"];
const BLOCK_FIELDS = [
  ["name", "Naam"], ["street", "Straat"], ["number", "Nr."], ["postal", "Postcode"], ["city", "Plaats"],
  ["mobile1", "Telefoon 1"], ["mobile2", "Telefoon 2"], ["email1", "E-mail 1"], ["email2", "E-mail 2"],
];

function GenderSelect({ value, onChange, testid }) {
  return (
    <Combobox testId={testid} value={value || ""} onChange={onChange} placeholder="Geslacht" options={GENDERS.map((g) => ({ value: g, label: g }))} />
  );
}

export default function TenantEditDialog({ open, onOpenChange, tenant, onSaved }) {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [catalog, setCatalog] = useState({ debtor_statuses: [], risk_profiles: [] });
  const [newStatus, setNewStatus] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setBlock = (bk, fk, v) => setForm((f) => ({ ...f, [bk]: { ...(f[bk] || {}), [fk]: v } }));
  const lookup = usePostcodeLookup((a, bk) => setForm((f) => (bk
    ? { ...f, [bk]: { ...(f[bk] || {}), street: a.street, city: a.city } }
    : { ...f, naw_street: a.street, naw_city: a.city })));
  const setBlockAddr = (bk, fk, v) => {
    setBlock(bk, fk, v);
    const b = form[bk] || {};
    if (fk === "postal") lookup(v, b.number, bk);
    if (fk === "number") lookup(b.postal, v, bk);
  };

  useEffect(() => { if (open && tenant) setForm({ ...tenant }); }, [open, tenant]);
  useEffect(() => { if (open) api.get("/catalog").then((r) => setCatalog(r.data)).catch(() => {}); }, [open]);

  const toggleStatus = (s) => {
    const cur = form.statuses || [];
    set("statuses", cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]);
  };
  const addNewStatus = async () => {
    const s = newStatus.trim();
    if (!s) return;
    if (!catalog.debtor_statuses.includes(s)) {
      const next = [...catalog.debtor_statuses, s];
      setCatalog((c) => ({ ...c, debtor_statuses: next }));
      api.put("/catalog", { debtor_statuses: next }).catch(() => {});
    }
    if (!(form.statuses || []).includes(s)) set("statuses", [...(form.statuses || []), s]);
    setNewStatus("");
  };

  const save = async () => {
    setLoading(true);
    try {
      await api.put(`/tenants/${tenant.id}`, { ...tenant, ...form, invoice_fee_rate: Number(form.invoice_fee_rate) || 0, default_interest_rate: Number(form.default_interest_rate) || 0, grace_huur: Number(form.grace_huur) || 0, grace_lening: Number(form.grace_lening) || 0, grace_factuur: Number(form.grace_factuur) || 0 });
      toast.success("Debiteurgegevens bijgewerkt");
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Bijwerken mislukt"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="tenant-edit-dialog">
        <DialogHeader>
          <DialogTitle>Debiteurgegevens bewerken</DialogTitle>
          <DialogDescription className="sr-only">Stamgegevens, adresblokken en vrije velden van de debiteur bewerken.</DialogDescription>
        </DialogHeader>

        <h3 className="text-sm font-semibold text-slate-700">Personen</h3>
        <div className="grid sm:grid-cols-3 gap-3">
          <div><Label className="text-xs">Voornaam (Persoon 1)</Label><Input value={form.first_name || ""} onChange={(e) => set("first_name", e.target.value)} data-testid="tenant-edit-first_name" /></div>
          <div><Label className="text-xs">Achternaam (Persoon 1)</Label><Input value={form.last_name || ""} onChange={(e) => set("last_name", e.target.value)} data-testid="tenant-edit-last_name" /></div>
          <div><Label className="text-xs">Geslacht (Persoon 1)</Label><GenderSelect value={form.gender1} onChange={(v) => set("gender1", v)} testid="tenant-edit-gender1" /></div>
          <div><Label className="text-xs">Voornaam (Persoon 2)</Label><Input value={form.first_name2 || ""} onChange={(e) => set("first_name2", e.target.value)} data-testid="tenant-edit-first_name2" /></div>
          <div><Label className="text-xs">Achternaam (Persoon 2)</Label><Input value={form.last_name2 || ""} onChange={(e) => set("last_name2", e.target.value)} data-testid="tenant-edit-last_name2" /></div>
          <div><Label className="text-xs">Geslacht (Persoon 2)</Label><GenderSelect value={form.gender2} onChange={(v) => set("gender2", v)} testid="tenant-edit-gender2" /></div>
        </div>

        <h3 className="text-sm font-semibold text-slate-700 mt-2">Adres & contact</h3>
        <AddressAutocomplete label="Zoek adres (autocomplete)" onSelect={(a) => setForm((f) => ({ ...f, naw_street: a.street, naw_number: a.number, naw_postal: a.postal_code, naw_city: a.city }))} />
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label className="text-xs">Straat</Label><Input value={form.naw_street || ""} onChange={(e) => set("naw_street", e.target.value)} data-testid="tenant-edit-naw_street" /></div>
          <div><Label className="text-xs">Nummer</Label><Input value={form.naw_number || ""} onChange={(e) => { set("naw_number", e.target.value); lookup(form.naw_postal, e.target.value); }} data-testid="tenant-edit-naw_number" /></div>
          <div><Label className="text-xs">Postcode</Label><Input value={form.naw_postal || ""} onChange={(e) => { set("naw_postal", e.target.value); lookup(e.target.value, form.naw_number); }} data-testid="tenant-edit-naw_postal" /></div>
          <div><Label className="text-xs">Plaats</Label><Input value={form.naw_city || ""} onChange={(e) => set("naw_city", e.target.value)} data-testid="tenant-edit-naw_city" /></div>
          <div><Label className="text-xs">E-mail 1</Label><Input value={form.email1 || ""} onChange={(e) => set("email1", e.target.value)} data-testid="tenant-edit-email1" /></div>
          <div><Label className="text-xs">E-mail 2</Label><Input value={form.email2 || ""} onChange={(e) => set("email2", e.target.value)} data-testid="tenant-edit-email2" /></div>
          <div><Label className="text-xs">Telefoon 1</Label><Input value={form.mobile1 || ""} onChange={(e) => set("mobile1", e.target.value)} onBlur={(e) => set("mobile1", normalizeNlPhone(e.target.value))} data-testid="tenant-edit-mobile1" /></div>
          <div><Label className="text-xs">Telefoon 2</Label><Input value={form.mobile2 || ""} onChange={(e) => set("mobile2", e.target.value)} onBlur={(e) => set("mobile2", normalizeNlPhone(e.target.value))} data-testid="tenant-edit-mobile2" /></div>
          <div><Label className="text-xs">IBAN</Label><Input value={form.iban || ""} onChange={(e) => set("iban", e.target.value)} data-testid="tenant-edit-iban" /></div>
        </div>

        {BLOCKS.map(([bk, label]) => {
          const isCompany = COMPANY_BLOCKS.includes(bk);
          return (
            <details key={bk} className="border rounded-lg p-3" data-testid={`tenant-edit-block-${bk}`}>
              <summary className="text-sm font-medium text-slate-700 cursor-pointer">{label}</summary>
              <div className="mt-3"><AddressAutocomplete label="Zoek adres (autocomplete)" onSelect={(a) => setForm((f) => ({ ...f, [bk]: { ...(f[bk] || {}), street: a.street, number: a.number, postal: a.postal_code, city: a.city } }))} /></div>
              {isCompany ? (
                <>
                  <div className="grid sm:grid-cols-3 gap-3 mt-3">
                    <div className="sm:col-span-3"><Label className="text-xs text-slate-500">Bedrijfsnaam</Label><Input value={(form[bk] || {}).name || ""} onChange={(e) => setBlock(bk, "name", e.target.value)} data-testid={`tenant-edit-${bk}-name`} /></div>
                    <div className="sm:col-span-2"><Label className="text-xs text-slate-500">Straat</Label><Input value={(form[bk] || {}).street || ""} onChange={(e) => setBlock(bk, "street", e.target.value)} data-testid={`tenant-edit-${bk}-street`} /></div>
                    <div><Label className="text-xs text-slate-500">Nr.</Label><Input value={(form[bk] || {}).number || ""} onChange={(e) => setBlockAddr(bk, "number", e.target.value)} data-testid={`tenant-edit-${bk}-number`} /></div>
                    <div><Label className="text-xs text-slate-500">Postcode</Label><Input value={(form[bk] || {}).postal || ""} onChange={(e) => setBlockAddr(bk, "postal", e.target.value)} data-testid={`tenant-edit-${bk}-postal`} /></div>
                    <div className="sm:col-span-2"><Label className="text-xs text-slate-500">Plaats</Label><Input value={(form[bk] || {}).city || ""} onChange={(e) => setBlock(bk, "city", e.target.value)} data-testid={`tenant-edit-${bk}-city`} /></div>
                  </div>
                  {[["contact1", "Contactpersoon 1"], ["contact2", "Contactpersoon 2"]].map(([cp, cl]) => (
                    <div key={cp} className="border-t pt-3 mt-3">
                      <Label className="text-xs font-semibold text-slate-600 mb-2 block">{cl}</Label>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div><Label className="text-xs text-slate-500">Naam</Label><Input value={(form[bk] || {})[`${cp}_name`] || ""} onChange={(e) => setBlock(bk, `${cp}_name`, e.target.value)} data-testid={`tenant-edit-${bk}-${cp}-name`} /></div>
                        <div><Label className="text-xs text-slate-500">Functie</Label><Input value={(form[bk] || {})[`${cp}_function`] || ""} onChange={(e) => setBlock(bk, `${cp}_function`, e.target.value)} data-testid={`tenant-edit-${bk}-${cp}-function`} /></div>
                        <div><Label className="text-xs text-slate-500">Telefoon</Label><Input value={(form[bk] || {})[`${cp}_phone`] || ""} onChange={(e) => setBlock(bk, `${cp}_phone`, e.target.value)} onBlur={(e) => setBlock(bk, `${cp}_phone`, normalizeNlPhone(e.target.value))} data-testid={`tenant-edit-${bk}-${cp}-phone`} /></div>
                        <div><Label className="text-xs text-slate-500">E-mail</Label><Input value={(form[bk] || {})[`${cp}_email`] || ""} onChange={(e) => setBlock(bk, `${cp}_email`, e.target.value)} data-testid={`tenant-edit-${bk}-${cp}-email`} /></div>
                      </div>
                    </div>
                  ))}
                  <div className="border-t pt-3 mt-3">
                    <Label className="text-xs font-semibold text-slate-600 mb-2 block">Extra contactpersonen</Label>
                    <Repeater items={(form[bk] || {}).contacts} onChange={(v) => setBlock(bk, "contacts", v)} testid={`tenant-edit-${bk}-contacts`}
                      fields={[{ key: "name", label: "Naam", full: true }, { key: "function", label: "Functie" }, { key: "phone", label: "Telefoon", normalize: true }, { key: "email", label: "E-mail", full: true }]}
                      addLabel="Contactpersoon toevoegen" emptyHint="Nog geen extra contactpersonen." />
                  </div>
                </>
              ) : (
                <div className="grid sm:grid-cols-3 gap-3 mt-3">
                  {BLOCK_FIELDS.map(([fk, fl]) => {
                    const isPhone = fk === "mobile1" || fk === "mobile2";
                    return (
                      <div key={fk}>
                        <Label className="text-xs text-slate-500">{fl}</Label>
                        <Input value={(form[bk] || {})[fk] || ""} onChange={(e) => setBlockAddr(bk, fk, e.target.value)} onBlur={isPhone ? (e) => setBlock(bk, fk, normalizeNlPhone(e.target.value)) : undefined} data-testid={`tenant-edit-${bk}-${fk}`} />
                      </div>
                    );
                  })}
                </div>
              )}
            </details>
          );
        })}

        <h3 className="text-sm font-semibold text-slate-700 mt-2">Extra velden (vrij)</h3>
        <Repeater items={form.custom_fields} onChange={(v) => set("custom_fields", v)} fields={[{ key: "label", label: "Veldnaam" }, { key: "value", label: "Waarde" }]} addLabel="Extra veld toevoegen" emptyHint="Voeg vrije velden toe (bijv. BSN-status, dossiernummer)." testid="tenant-edit-custom-fields" />

        <h3 className="text-sm font-semibold text-slate-700 mt-2">Statussen</h3>
        <div className="flex flex-wrap gap-2" data-testid="tenant-edit-statuses">
          {catalog.debtor_statuses.map((s) => {
            const on = (form.statuses || []).includes(s);
            return (
              <button key={s} type="button" onClick={() => toggleStatus(s)} data-testid={`tenant-status-${s}`}
                className={`px-3 py-1 rounded-full text-sm border transition-colors ${on ? "bg-blue-900 text-white border-blue-900" : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"}`}>
                {s}
              </button>
            );
          })}
        </div>
        <div className="flex gap-2">
          <Input value={newStatus} onChange={(e) => setNewStatus(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addNewStatus(); } }} placeholder="Nieuwe status toevoegen…" data-testid="tenant-new-status-input" />
          <Button type="button" variant="outline" onClick={addNewStatus} data-testid="tenant-new-status-add"><Plus className="h-4 w-4 mr-1" /> Toevoegen</Button>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-slate-700 mt-2 mb-1">Risicoprofiel</h3>
          <Combobox testId="tenant-edit-risk-profile" value={form.risk_profile || "none"} onChange={(v) => set("risk_profile", v === "none" ? "" : v)} placeholder="Geen profiel" options={[{ value: "none", label: "— Geen —" }, ...catalog.risk_profiles.map((p) => ({ value: p.name, label: p.name }))]} />
        </div>


        {!(catalog.hidden_modules || []).includes("tab-betalingen") && (
        <>
        <h3 className="text-sm font-semibold text-slate-700 mt-2">Standaardwaarden & coulance</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label className="text-xs">Standaard factoring fee (%)</Label><Input type="number" value={form.invoice_fee_rate ?? ""} onChange={(e) => set("invoice_fee_rate", e.target.value)} data-testid="tenant-edit-fee" /></div>
          <div><Label className="text-xs">Standaard rente percentage (%)</Label><Input type="number" value={form.default_interest_rate ?? ""} onChange={(e) => set("default_interest_rate", e.target.value)} data-testid="tenant-edit-interest" /></div>
          <div><Label className="text-xs">Coulancedagen huur</Label><Input type="number" value={form.grace_huur ?? ""} onChange={(e) => set("grace_huur", e.target.value)} data-testid="tenant-edit-grace-huur" /></div>
          <div><Label className="text-xs">Coulancedagen lening</Label><Input type="number" value={form.grace_lening ?? ""} onChange={(e) => set("grace_lening", e.target.value)} data-testid="tenant-edit-grace-lening" /></div>
          <div><Label className="text-xs">Coulancedagen factuur/factoring</Label><Input type="number" value={form.grace_factuur ?? ""} onChange={(e) => set("grace_factuur", e.target.value)} data-testid="tenant-edit-grace-factuur" /></div>
        </div>
        </>
        )}
        <div><Label className="text-xs">Notities</Label><Textarea rows={2} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} data-testid="tenant-edit-notes" /></div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuleren</Button>
          <Button className="bg-blue-900 hover:bg-blue-800" onClick={save} disabled={loading} data-testid="tenant-edit-save">{loading ? "Opslaan..." : "Opslaan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
